// THE single source of truth for "is a quote line's config valid against the
// active form schema?" — shared verbatim by every surface that judges a config:
//   - the portal pre-flight before "See my price" (QuoteEdit.handleGetPricing),
//   - the portal "Save" report (QuoteEdit.handleSave) — Save still persists, but
//     it now reports the SAME issues the price path would (saves stay permissive
//     and route to sales),
//   - the authoritative backend price-side gate (services/pricing) — so the
//     portal returns the SAME field_keys whether pricing is mocked or real, and
//     an obviously-invalid config never reaches Nexus.
//
// This mirrors the shipToMissingFieldKeys / orderServicesMissingFieldKeys pattern
// in vocabulary.ts: one function, two+ callers, "so the two can never disagree."
// Before this existed, Save validated NOTHING (always "green") while the price
// path validated separately — the contradiction that motivated unifying them.
//
// Scope: this validates the form-schema vocabulary the PORTAL masters (required
// fields, conditional visibility, accepted option set). It does NOT price and
// does NOT replace Nexus — Nexus still owns cost/markup/tier. The accepted set
// it checks is the schema's own enum_options (the portal-mastered vocabulary),
// which is exactly what Nexus must mirror; if they drift, that's the
// portal→Nexus sync hazard documented in docs/ADR-011 and CLAUDE.md.
//
// One thing it judges is not schema-shaped: an assembly ITEM's kit, which lives
// nested on the line's own config rather than as a field. See the block at the
// end of lineConfigIssues for why it is here and not beside it.
import { evalExpr } from './form-predicate.js';
import { fieldScope, groupScopeByKey, DEFAULT_FORM_FIELD_GROUP_SCOPE } from './form-scope.js';
import { readLineProductType } from './product-type.js';
import { ASSEMBLY_FIELD_LABELS, ASSEMBLY_ITEM_CONFIG_KEY, ASSEMBLY_ITEM_PRODUCT_TYPE, assemblyMissingFieldKeys, LINE_TITLE_FIELD_KEY, LINE_TITLE_FIELD_LABEL, normalizeAssemblySnapshot, normalizeInsertRows, } from '../vocabulary.js';
// Is a field currently shown for this config? conditional_show is an AND across
// all expressions — the EXACT gate the SchemaForm renderer applies, so a field
// hidden in the UI is never counted as missing/invalid here.
export function isFieldVisible(field, config) {
    return (field.conditional_show.length === 0 ||
        field.conditional_show.every((c) => evalExpr(c.expression, config)));
}
// The accepted option-value set for an enum/multi_enum field, or null for
// non-enum types (or an enum whose options were all visibility-filtered out, in
// which case we can't validate membership).
//
// We intentionally use the FULL served enum_options set and DO NOT consult
// per-option `meta.applies_to` / `meta.hide_when` here — those are RENDERER-only
// gates. Enforcing them would wrongly flag valid cross-family data (e.g. a clone
// that still carries a single_sheet-only size on a book line) as invalid_value.
// The superset is the safe direction: the validator never rejects a value the
// renderer would have allowed. Do not narrow this with applies_to.
function acceptedValues(field) {
    if (field.type !== 'enum' && field.type !== 'multi_enum')
        return null;
    if (field.enum_options.length === 0)
        return null;
    return new Set(field.enum_options.map((o) => o.value));
}
function failsPatternRule(field, value, config) {
    return field.validation_rules.some((rule) => {
        if (rule.rule_type !== 'pattern' || rule.severity !== 'error')
            return false;
        if (rule.applies_when != null && !evalExpr(rule.applies_when, config))
            return false;
        const pattern = rule.rule_value?.pattern;
        if (typeof pattern !== 'string' || pattern === '')
            return false;
        try {
            return !new RegExp(pattern).test(value);
        }
        catch {
            return false;
        }
    });
}
// Validate one line's config against the active schema. Returns every issue
// found (empty ⇒ the config is complete + valid for pricing). Order follows the
// schema's field order so the caller's "jump to the first problem" lands on the
// earliest field on the form.
export function lineConfigIssues(schema, config, options) {
    const issues = [];
    const scope = options?.scope ?? DEFAULT_FORM_FIELD_GROUP_SCOPE;
    const scopeByGroupKey = groupScopeByKey(schema);
    for (const field of schema.fields) {
        // Fields of another grain are not this config's to answer for.
        if (fieldScope(field, scopeByGroupKey) !== scope)
            continue;
        // file_reference fields never live in `config` (uploads are Asset rows), so
        // this config validator can't judge them. Required-artwork readiness IS now a
        // pricing gate — but it's enforced on the upload axis by
        // lineMissingUploadFieldKeys (upload-validation.ts), which reads Asset state
        // and reuses validateRoleSet (the submit gate's rule). The two run side by
        // side at price time; keep upload checks THERE, not here.
        if (field.type === 'file_reference')
            continue;
        // Skip fields not currently shown to the customer.
        if (!isFieldVisible(field, config))
            continue;
        const v = config[field.key];
        const accepted = acceptedValues(field);
        if (field.type === 'multi_enum') {
            // Satisfied only when at least one CURRENTLY-OFFERED option is selected —
            // an empty array, or one holding only stale/removed values (e.g. a legacy
            // proof_type of ['none'], now retired), counts as missing.
            const ok = Array.isArray(v)
                ? accepted
                    ? v.some((sel) => accepted.has(sel))
                    : v.length > 0
                : false;
            if (!ok && field.required) {
                issues.push({ field_key: field.key, field_label: field.label, reason: 'missing' });
            }
            continue;
        }
        // A row list is present when it holds a row the save would keep — the same
        // normalizer the write boundary persists through, so an unfinished row is
        // missing here exactly when the save would drop it.
        const present = field.type === 'row_list'
            ? normalizeInsertRows(v).length > 0
            : v !== undefined && v !== null && v !== '';
        if (!present) {
            if (field.required) {
                issues.push({ field_key: field.key, field_label: field.label, reason: 'missing' });
            }
            continue;
        }
        // A present scalar enum value must be in the accepted set. This is what
        // makes validation identical whether pricing is mocked or real: the portal
        // catches a stale/invalid option itself instead of waiting for Nexus to
        // reject it.
        if (accepted && !accepted.has(v)) {
            issues.push({ field_key: field.key, field_label: field.label, reason: 'invalid_value' });
            continue;
        }
        // A seeded `pattern` error rule is judged HERE as well as in the renderer's
        // runRule, so the backend price gate refuses the value the form flags — a
        // priced order carries a customer-typed share link only once it is https.
        if (typeof v === 'string' && failsPatternRule(field, v, config)) {
            issues.push({ field_key: field.key, field_label: field.label, reason: 'invalid_value' });
        }
    }
    // The item's NAME, which is a QuoteLine column rather than a schema field and
    // so has no `schema.fields` entry for the loop above to reach. It is judged
    // here, inside the one validator, for the same reason the assembly kit is: the
    // SPA pre-flight, the Save report and the backend price gate all call this
    // function, so none of them can be missing a check the others make.
    //
    // Reported at the END of the line's issues and re-sorted by the caller's field
    // display_order; it carries none, so it sorts last — the name is the least
    // specced-out thing on the item and "jump to the first problem" should land on
    // a spec field when one is also missing.
    if (scope === 'line' && options?.lineTitle !== undefined) {
        if ((options.lineTitle ?? '').trim() === '') {
            issues.push({
                field_key: LINE_TITLE_FIELD_KEY,
                field_label: LINE_TITLE_FIELD_LABEL,
                reason: 'missing',
            });
        }
    }
    // An ASSEMBLY item's kit, which is part of this line's config and which no
    // `schema.fields` entry describes: it lives at `config.assembly` as an
    // AssemblySnapshot and is judged by `assemblyMissingFieldKeys` (R12).
    //
    // It belongs INSIDE this function rather than beside it because every surface
    // that judges a line already calls this one: the SPA pre-flight, the Save
    // report and the backend price gate pick the misses up with no new call site
    // to forget. The upload axis is the one that legitimately sits alongside, and
    // for a reason that does not apply here — it reads Asset state this validator
    // cannot see, while the kit is already in the config it was handed.
    //
    // Judged only at LINE scope: a quote-scope caller is asking about the packing
    // axes as seeded fields, and reporting the kit there too would put one
    // customer answer behind two field keys. How many kits is the item's own
    // QuoteLine.quantity, so the kit snapshot carries no count to require.
    if (scope === 'line' && readLineProductType(config) === ASSEMBLY_ITEM_PRODUCT_TYPE) {
        const assembly = normalizeAssemblySnapshot(config[ASSEMBLY_ITEM_CONFIG_KEY]);
        for (const key of assemblyMissingFieldKeys(assembly)) {
            issues.push({
                field_key: key,
                field_label: ASSEMBLY_FIELD_LABELS[key] ?? key,
                reason: 'missing',
            });
        }
    }
    return issues;
}
// Convenience: just the offending field keys (e.g. for a PricingFailureDto
// details.field_keys list). De-duplicated, schema order preserved.
export function lineConfigMissingFieldKeys(schema, config, options) {
    const seen = new Set();
    const keys = [];
    for (const issue of lineConfigIssues(schema, config, options)) {
        if (seen.has(issue.field_key))
            continue;
        seen.add(issue.field_key);
        keys.push(issue.field_key);
    }
    return keys;
}
//# sourceMappingURL=form-validation.js.map